// src/components/shared/useBulkSelection.js
// Hook genérico de seleção em lote — 100% agnóstico de tabela/entidade.
// Funciona com qualquer id estável (uuid, número, slug...).
//
// API:
//   const sel = useBulkSelection();
//   sel.toggle(id)
//   sel.selectAll(ids[])         // adiciona os ids passados (não todos do banco)
//   sel.deselectAll()
//   sel.isSelected(id) → boolean
//   sel.count                    // selectedIds.size
//   sel.getSelectedIds() → string[]
//
// Decisões:
//   - Set interno (O(1) toggle/has).
//   - Estado é re-criado a cada mutation (immutable update → React detecta).
//   - selectAll merge: adiciona aos atuais (não substitui). Use deselectAll antes
//     se quiser reset.

import { useCallback, useMemo, useState } from 'react';

export default function useBulkSelection(initialIds = []) {
  const [selectedIds, setSelectedIds] = useState(() => new Set(initialIds));

  const toggle = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback((ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
  }, []);

  const deselectAll = useCallback(() => {
    setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);

  const isSelected = useCallback((id) => selectedIds.has(id), [selectedIds]);

  const getSelectedIds = useCallback(() => Array.from(selectedIds), [selectedIds]);

  // count via useMemo para estabilidade entre renders quando o Set é o mesmo.
  const count = useMemo(() => selectedIds.size, [selectedIds]);

  return {
    selectedIds,
    toggle,
    selectAll,
    deselectAll,
    isSelected,
    count,
    getSelectedIds,
  };
}
